import React, { createContext, useContext, useCallback, useMemo, useState } from "react";
import { CONFIGURE_BOQ_STORAGE_KEY } from "../config/dynamicSummaryConfig";

// Off by default (no build-time env flag feeds this any more, unlike
// DevModeContext.tsx's VITE_DEV_MODE -> devMode default) — a user must
// explicitly flip the "Configure BOQ" switch next to the Re-calculate
// button on /groundworks/review to enter "add sections/items" mode; that
// choice then persists per browser via localStorage.
function getInitialConfigureBoq(): boolean {
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(CONFIGURE_BOQ_STORAGE_KEY);
    if (stored !== null) return stored === "true";
  }
  return false;
}

interface ConfigureBoqContextType {
  configureBoq: boolean;
  setConfigureBoq: (value: boolean) => void;
  toggleConfigureBoq: () => void;
}

const ConfigureBoqContext = createContext<ConfigureBoqContextType | null>(null);

export const useConfigureBoqContext = (): ConfigureBoqContextType => {
  const context = useContext(ConfigureBoqContext);
  if (context === null) {
    throw new Error("useConfigureBoqContext must be used within a ConfigureBoqProvider");
  }
  return context;
};

// Convenience hook for the common case of just reading the flag — used
// throughout the groundworks Bill Summary components for their own
// "Add Section"/"Add Item" gating.
export const useConfigureBoq = (): boolean => useConfigureBoqContext().configureBoq;

export const ConfigureBoqProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [configureBoq, setConfigureBoqState] = useState<boolean>(getInitialConfigureBoq);

  const setConfigureBoq = useCallback((value: boolean) => {
    setConfigureBoqState(value);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CONFIGURE_BOQ_STORAGE_KEY, String(value));
    }
  }, []);

  const toggleConfigureBoq = useCallback(() => {
    setConfigureBoq(!configureBoq);
  }, [configureBoq, setConfigureBoq]);

  const value = useMemo(
    () => ({ configureBoq, setConfigureBoq, toggleConfigureBoq }),
    [configureBoq, setConfigureBoq, toggleConfigureBoq],
  );

  return (
    <ConfigureBoqContext.Provider value={value}>{children}</ConfigureBoqContext.Provider>
  );
};
